import { OnwardLink } from '@/styles/blocks';
import { format } from 'date-fns';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '@/styles';
import { css } from '@emotion/react';
import { DISPOSITION_LABELS } from '@/components/ShipmentForm/constants';

const TRACKING_EVENT_LABELS = {
    ITEM_RECEIVED_INTO_WAREHOUSE: 'Final Mile - Received',
    ITEM_STORED_IN_WAREHOUSE: 'Final Mile - Stored',
    INVENTORY_ITEM_ALLOCATED_TO_DELIVERY: 'Final Mile - Allocated',
    ITEM_PICKED_FROM_WAREHOUSE: 'Final Mile - Picked',
    ITEM_LOADED_FROM_WAREHOUSE: 'Final Mile - Loaded',
    ITEM_DELIVERED: 'Final Mile - Delivered',
};

// Intangible items are informational only (no physical footprint), so they are never received,
// loaded or delivered and never receive the events the handling indicators below look for. They
// render as not applicable, distinct from both a failure and a stage the order has yet to reach.
const NOT_APPLICABLE = (
    <span
        css={css`
            color: ${colors.greys[0]};
        `}
    >
        &mdash;
    </span>
);

export const COLUMNS = [
    {
        Header: '',
        id: 'item-details-group',
        columns: [
            {
                id: 'barcode',
                Header: 'Barcode',
                accessor: (item) => item.barcode,
                width: 150,
                sortType: 'basic',
            },
            {
                id: 'sku',
                Header: 'SKU',
                accessor: (item) => item.sku,
                width: 150,
                sortType: 'basic',
            },
            {
                id: 'line_id',
                Header: 'Line ID',
                accessor: (item) => item.line_id,
                width: 150,
                sortType: 'basic',
            },
            {
                id: 'serial_number',
                Header: 'Serial Number',
                accessor: (item) => item.serial_number,
                width: 150,
                sortType: 'basic',
            },
            {
                id: 'item_type_details',
                Header: 'Type',
                accessor: (item) => item.item_type_details,
                width: 150,
                sortType: 'basic',
            },
            {
                id: 'quantity',
                Header: 'Quantity',
                accessor: (item) => parseInt(item.quantity),
                width: 100,
                sortType: 'basic',
            },
            {
                id: 'total_cubes',
                Header: 'Cubes (cu ft)',
                accessor: (item) => (item.total_cubes ? parseFloat(item.total_cubes).toFixed(2) : ''),
                width: 125,
                sortType: 'basic',
            },
            {
                id: 'total_weight',
                Header: 'Weight (lbs)',
                accessor: (item) => (item.total_weight ? parseFloat(item.total_weight).toFixed(2) : ''),
                width: 125,
                sortType: 'basic',
            },
            {
                id: 'dimensions',
                Header: 'Dimensions (in)',
                accessor: (item) =>
                    [
                        [item.length, 'L'],
                        [item.width, 'W'],
                        [item.height, 'H'],
                    ]
                        .map(([value, label]) => `${parseInt(value)}${label}`)
                        .join(' '),
                width: 200,
                sortType: 'basic',
            },
            {
                id: 'desc',
                Header: 'Description',
                accessor: (item) => item.description,
                Cell: ({ value }) => (
                    <div
                        css={css`
                            overflow: hidden;
                            text-overflow: ellipsis;
                            max-width: 250px;
                        `}
                    >
                        {value}
                    </div>
                ),
                width: 300,
                sortType: 'basic',
            },
            {
                id: 'disposition',
                Header: 'Disposition',
                accessor: (item) => (item.is_return ? DISPOSITION_LABELS[item.disposition] || 'Unknown' : ''),
                width: 200,
                sortType: 'basic',
            },
            {
                id: 'pic',
                Header: 'Image',
                accessor: (item) => item.pic,
                Cell: ({ value }) =>
                    value ? (
                        <OnwardLink href={value} target="_blank">
                            View
                        </OnwardLink>
                    ) : (
                        ''
                    ),
                width: 120,
            },
            {
                id: 'received_time',
                Header: 'Received Time',
                accessor: (item) => {
                    const event = (item.tracking_events || []).find(
                        (e) => e.event_type === 'ITEM_RECEIVED_INTO_WAREHOUSE'
                    );
                    return event?.event_time;
                },
                Cell: ({ value }) => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss a') : ''),
                width: 200,
                sortType: 'basic',
            },
            {
                id: 'loaded_time',
                Header: 'Loaded Time',
                accessor: (item) => {
                    const event = (item.tracking_events || []).find(
                        (e) => e.event_type === 'ITEM_LOADED_FROM_WAREHOUSE'
                    );
                    return event?.event_time;
                },
                Cell: ({ value }) => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss a') : ''),
                width: 200,
                sortType: 'basic',
            },
            {
                id: 'delivered_time',
                Header: 'Delivered Time',
                accessor: (item) => {
                    const event = (item.tracking_events || []).find((e) => e.event_type === 'ITEM_DELIVERED');
                    return event?.event_time;
                },
                Cell: ({ value }) => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss a') : ''),
                width: 200,
                sortType: 'basic',
            },
            {
                id: 'geolocation',
                Header: 'Geolocation',
                accessor: (item) => item.last_tracking_event,
                Cell: ({ value, callbacks }) => {
                    if (!value?.event_lat || !value?.event_lng) {
                        return '';
                    }
                    return (
                        <OnwardLink
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                callbacks.setMapParams({ waypoints: [{ lat: value.event_lat, lng: value.event_lng }] });
                            }}
                            href={`https://www.google.com/maps/search/?api=1&query=${value.event_lat},${value.event_lng}`}
                            target="_blank"
                        >
                            Lat: {value.event_lat} Lng: {value.event_lng}
                        </OnwardLink>
                    );
                },
                width: 300,
            },
            {
                id: 'confirmation',
                Header: 'Confirmation',
                accessor: (item) => item.last_tracking_event,
                Cell: ({ value }) => {
                    const display = {
                        SCAN: 'Scanned',
                        MANUAL: 'Manual Entry',
                        UNKNOWN: 'Unknown',
                    };
                    return value ? display[value?.event_source] || 'Unknown' : '';
                },
                width: 165,
            },
            {
                id: 'on_hand',
                Header: 'On Hand',
                accessor: (item) =>
                    (item.tracking_events || []).find((e) => e.event_type === 'ITEM_RECEIVED_INTO_WAREHOUSE'),
                Cell: ({ value, row }) => {
                    if (row.original.intangible) {
                        return NOT_APPLICABLE;
                    }

                    const isOrderReceived = (row.original.order.wh_events || []).find(
                        (e) => e.transition?.next == 'RECEIVING'
                    );

                    return !!value ? (
                        <CheckIcon
                            css={css`
                                color: ${colors.greens.primary};
                            `}
                        />
                    ) : isOrderReceived ? (
                        <CloseIcon
                            css={css`
                                color: ${colors.reds.primary};
                            `}
                        />
                    ) : (
                        ''
                    );
                },
                width: 130,
            },
            {
                id: 'loaded',
                Header: 'Loaded',
                accessor: (item) =>
                    (item.tracking_events || []).find((e) => e.event_type === 'ITEM_LOADED_FROM_WAREHOUSE'),
                Cell: ({ value, row }) => {
                    if (row.original.intangible) {
                        return NOT_APPLICABLE;
                    }

                    const isOrderLoaded = ['inProgress', 'complete'].includes(row.original.order.order_status);

                    return !!value ? (
                        <CheckIcon
                            css={css`
                                color: ${colors.greens.primary};
                            `}
                        />
                    ) : isOrderLoaded ? (
                        <CloseIcon
                            css={css`
                                color: ${colors.reds.primary};
                            `}
                        />
                    ) : (
                        ''
                    );
                },
                width: 120,
            },
            {
                id: 'delivered',
                Header: 'Delivered',
                accessor: (item) => (item.tracking_events || []).find((e) => e.event_type === 'ITEM_DELIVERED'),
                Cell: ({ value, row }) => {
                    if (row.original.intangible) {
                        return NOT_APPLICABLE;
                    }

                    const isOrderDelivered = ['complete'].includes(row.original.order.order_status);

                    return !!value ? (
                        <CheckIcon
                            css={css`
                                color: ${colors.greens.primary};
                            `}
                        />
                    ) : isOrderDelivered ? (
                        <CloseIcon
                            css={css`
                                color: ${colors.reds.primary};
                            `}
                        />
                    ) : (
                        ''
                    );
                },
                width: 130,
            },
        ],
    },
    {
        Header: '',
        id: 'sticky-right',
        sticky: 'right',
        columns: [
            {
                id: 'status',
                Header: 'Status',
                accessor: (item) => item.last_tracking_event,
                Cell: ({ value, row }) => {
                    if (row.original.isResolutionRemoved) {
                        return (
                            <span
                                css={css`
                                    color: ${colors.reds.primary};
                                `}
                            >
                                Removed via Resolution
                            </span>
                        );
                    }
                    return value ? TRACKING_EVENT_LABELS[value?.event_type] || 'Unknown' : '';
                },
                width: 150,
            },
            {
                id: 'show_hidden_columns',
                Header: '',
                accessor: () => '',
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                disableSortBy: true,
                disableResizing: true,
            },
        ],
    },
];
